// What the draft room works out for a drafter from the view it holds
// (PLAN-2026-10-14 §3). Counting, never judging: how far away my pick is,
// who picks before it, how many are left in a tier, what the last picks took,
// which of my players share a bye. Nothing here says who to take
// (docs/decisions/2026-09-12-tools-not-instructions.md).
//
// PRESENTATION, NOT RULES. Every number is a count over keys the room sent —
// the board, the available list, the club byes — so it is a client's own
// business, and a client in another language counts the same keys.

import type { DraftPlayerRow, DraftView } from '@fsp/domain';

/** The overall number of my next pick: now if I am on the clock. Null with no seat or none left. */
export function myNextOverall(view: DraftView): number | null {
  if (view.myTeamId === null) return null;
  const from = view.current?.overall ?? 1;
  const cell = view.board.find(
    (c) => c.overall >= from && c.teamId === view.myTeamId && c.player === null,
  );
  return cell?.overall ?? null;
}

/** How many picks come before mine: 0 when I am on the clock. */
export function picksUntilMine(view: DraftView): number | null {
  const next = myNextOverall(view);
  if (next === null || view.current === null) return null;
  return next - view.current.overall;
}

/** The teams that pick between now and my next turn, in the order they pick. */
export function teamsBeforeMine(view: DraftView): string[] {
  const next = myNextOverall(view);
  const now = view.current?.overall;
  if (next === null || now === undefined) return [];
  return view.board
    .filter((c) => c.overall >= now && c.overall < next && c.player === null)
    .map((c) => c.teamId);
}

/** How many of each position every team has drafted, by team id. */
export function positionCounts(view: DraftView): Map<string, Record<string, number>> {
  const out = new Map<string, Record<string, number>>(view.seats.map((s) => [s.team.id, {}]));
  for (const c of view.board) {
    const pos = c.player?.positions[0];
    if (pos === undefined) continue;
    // WHO HAS HIM NOW, a trade's included.
    const team = c.heldBy ?? c.teamId;
    const row = out.get(team) ?? {};
    row[pos] = (row[pos] ?? 0) + 1;
    out.set(team, row);
  }
  return out;
}

export interface TierLeft {
  position: string;
  /** The best tier with somebody still in it. */
  tier: number;
  /** How many are left in it. */
  left: number;
  /** How many are in the tier after it, or null when there is none. */
  nextLeft: number | null;
}

/** Per position, the best tier still open and how many are left in it, in the order given. */
export function tiersLeft(
  available: readonly DraftPlayerRow[],
  positions: readonly string[],
): TierLeft[] {
  return positions.flatMap((position) => {
    const rows = available.filter((r) => r.player.positions[0] === position);
    if (rows.length === 0) return [];
    const tier = Math.min(...rows.map((r) => r.tier));
    const left = rows.filter((r) => r.tier === tier).length;
    const after = rows.filter((r) => r.tier > tier);
    const nextTier = after.length === 0 ? null : Math.min(...after.map((r) => r.tier));
    return [
      {
        position,
        tier,
        left,
        nextLeft: nextTier === null ? null : rows.filter((r) => r.tier === nextTier).length,
      },
    ];
  });
}

/** What the last `n` picks took, by position, most first. */
export function recentRun(view: DraftView, n = 10): { position: string; count: number }[] {
  const made = view.board
    .filter((c) => c.player !== null)
    .sort((a, b) => b.overall - a.overall)
    .slice(0, n);
  const counts = new Map<string, number>();
  for (const c of made) {
    const pos = c.player!.positions[0] ?? '';
    counts.set(pos, (counts.get(pos) ?? 0) + 1);
  }
  return [...counts]
    .map(([position, count]) => ({ position, count }))
    .sort((a, b) => b.count - a.count || a.position.localeCompare(b.position));
}

/** The weeks two or more of my players are on a bye together, earliest first. */
export function sharedByes(view: DraftView): { week: number; names: string[] }[] {
  const byWeek = new Map<number, string[]>();
  for (const row of view.myRoster) {
    const p = row.player;
    if (p === null || p.proTeam === null) continue;
    const week = view.byeWeeks[p.proTeam];
    if (week === undefined) continue;
    byWeek.set(week, [...(byWeek.get(week) ?? []), p.shortName]);
  }
  return [...byWeek]
    .filter(([, names]) => names.length > 1)
    .map(([week, names]) => ({ week, names }))
    .sort((a, b) => a.week - b.week);
}

/** "2.07": the round and the pick within it, as drafters write it. */
export function pickLabel(round: number, slot: number): string {
  return `${String(round)}.${String(slot).padStart(2, '0')}`;
}
